import type { ThumbnailStyle, ThumbnailStyleId, StyleSelection } from '@/@types/aiStyle';
import { AI_STYLE_THUMBNAILS } from '@/constants/aiStyleThumbnails';

class AiStyleManager {
  private readonly styleMap: ReadonlyMap<ThumbnailStyleId, ThumbnailStyle>;

  constructor(styles: readonly ThumbnailStyle[]) {
    this.styleMap = new Map(styles.map((style) => [style.id, style]));
  }

  getStyleById(id: ThumbnailStyleId | null): ThumbnailStyle | null {
    if (!id) return null;
    return this.styleMap.get(id) ?? null;
  }

  getStylePrompt(id: ThumbnailStyleId | null): string {
    const style = this.getStyleById(id);
    return style?.imageStylePrompt ?? '';
  }

  getStyleTitle(id: ThumbnailStyleId | null): string {
    const style = this.getStyleById(id);
    return style?.title ?? '';
  }

  getStyleTitleEn(id: ThumbnailStyleId | null): string {
    const style = this.getStyleById(id);
    return style?.titleEn ?? '';
  }

  getStyleSelection(id: ThumbnailStyleId | null): StyleSelection {
    return {
      selectedId: id,
      stylePrompt: this.getStylePrompt(id),
      styleTitle: this.getStyleTitle(id)
    };
  }

  static getAllStyles(): readonly ThumbnailStyle[] {
    return AI_STYLE_THUMBNAILS;
  }

  isValidStyleId(id: string | null): id is ThumbnailStyleId {
    return id !== null && this.styleMap.has(id as ThumbnailStyleId);
  }
}

// 인스턴스 생성
const aiStyleManagerInstance = new AiStyleManager(AI_STYLE_THUMBNAILS);

// Export with both instance methods and static methods
export const aiStyleManager = {
  getStyleById: (id: ThumbnailStyleId | null) => aiStyleManagerInstance.getStyleById(id),
  getStylePrompt: (id: ThumbnailStyleId | null) => aiStyleManagerInstance.getStylePrompt(id),
  getStyleTitle: (id: ThumbnailStyleId | null) => aiStyleManagerInstance.getStyleTitle(id),
  getStyleTitleEn: (id: ThumbnailStyleId | null) => aiStyleManagerInstance.getStyleTitleEn(id),
  getStyleSelection: (id: ThumbnailStyleId | null) => aiStyleManagerInstance.getStyleSelection(id),
  getAllStyles: () => AiStyleManager.getAllStyles(),
  isValidStyleId: (id: string | null) => aiStyleManagerInstance.isValidStyleId(id)
};
